<template>
  <div class="tw-flex tw-flex-col">
    <base-button-toggle-group ref="statusToggle" size="sm" :options="statusFilters" @selected="statusFilterSelected" />
    <base-table :loading="isLoading" :fields="fields" :items="items" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" class="tw-mt-4">
      <template #user="{item: invite}">
        <div class="tw-whitespace-nowrap">{{ invite.name }}</div>
        <div class="tw-whitespace-nowrap tw-text-gray-500 tw-text-xs">{{ invite.email }}</div>
      </template>
      <template #role="{item: invite}">
        <div v-for="role in invite.roles" :key="role" class="tw-whitespace-nowrap">{{ roleName(role) }}</div>
      </template>
      <template #locations="{item: invite}">
        <div v-if="invite.locations.length > 0">
          <div v-for="location in invite.locations" :key="location" class="tw-whitespace-nowrap tw-text-xs">{{ location }}</div>
        </div>
        <div v-else class="tw-whitespace-nowrap">All Locations</div>
      </template>
      <template #status="{item: invite}">
        <div class="tw-whitespace-nowrap">{{ capitalize(invite.status) }}</div>
      </template>
      <template #actions="{item: invite}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="resend(invite)">
            <template v-slot:icon>
              <arrow-renew-icon class="tw-w-3 tw-h-3" /> </template
            >Resend
          </base-button>
          <base-button type="flat" @click="del(invite)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-60" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-gap-3">
            <base-skeleton-loader class="tw-h-8 tw-w-18" />
            <base-skeleton-loader class="tw-h-8 tw-w-18" />
          </div>
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <business-user-invite-delete :businessId="businessId" ref="delete" @close="refresh" />
  </div>
</template>

<script>
import { ArrowRenewIcon, TrashIcon } from "@/assets/svgs";
import BusinessUserInviteDelete from "@/views/business/business-users/components/BusinessUserInviteDelete";
import { capitalize } from "lodash";
import { ROLE_OPTIONS } from "@/constants/constants";

const TAKE_KEY = "predictile_business_user_invites_take";

export default {
  components: {
    BusinessUserInviteDelete,
    ArrowRenewIcon,
    TrashIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isLoading: false,
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      status: "pending",
      statusFilters: [{ label: "Pending", value: "pending" }, { label: "Accepted", value: "accepted" }, { label: "Rejected", value: "rejected" }],
      sortKey: "user",
      sortDesc: false,
    };
  },
  computed: {
    fields() {
      let fieldList = [];
      fieldList.push({ title: "User", key: "user", thClass: "", isSortable: true });
      fieldList.push({ title: "Role", key: "role", thClass: "" });
      fieldList.push({ title: "Locations", key: "locations", thClass: "", isSortable: true });
      fieldList.push({ title: "Status", key: "status", thClass: "" });
      if (this.status === "pending") fieldList.push({ title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" });
      return fieldList;
    },
  },
  created() {
    this.EventBus.$on("business_user_invite_sent", this.loadPage);
  },
  mounted() {
    this.$refs.statusToggle.handleClick("pending");
    this.loadPage();
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    status() {
      this.loadPage();
    },

    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  methods: {
    capitalize,
    roleName(roleValue) {
      return roleValue ? ROLE_OPTIONS.find((r) => r.value === roleValue).label : "";
    },
    del(item) {
      this.$refs.delete.show(item.businessUserInviteId);
    },
    resend(item) {
      this.$axios
        .post(`/businesses/${this.businessId}/user-invites/${item.businessUserInviteId}/resend`)
        .then(() => {
          this.refresh();
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Invitation Resent", message: "The invitation was resent to the user." });
        })
        .catch(() => {});
    },
    refresh() {
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      let url = `/businesses/${this.businessId}/user-invites`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.status) url += `&status=${encodeURIComponent(this.status)}`;
      if (this.sortKey) url += `&sortColumn=${encodeURIComponent(this.sortKey)}`;
      if (this.sortKey) url += `&sortOrder=${this.sortDesc === true ? "desc" : "asc"}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.items = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch(() => {});
    },
    statusFilterSelected(value) {
      this.page.take = 50;
      this.page.skip = 0;
      this.page.total = 0;
      this.status = value;
    },
  },
};
</script>
